import { useEffect, useState } from 'react'
import { useMutation, useQuery } from 'urql'
import type { PacketContent } from 'app/components/form-runtime/PacketRunner'
import type { Appearance } from 'app/lib/forms/appearance'
import type { FormVersions, PacketBranding, PacketFormRef, PacketScreen } from 'app/lib/forms/builder'
import {
  groupFormVersions,
  moveItem,
  parseAppearance,
  parseBranding,
  parsePacketForms,
  parseQuestions,
  parseScreen,
  sameDraft,
} from 'app/lib/forms/builder'
import type { FormCategory } from 'app/lib/forms/types'
import { useUnsavedChangesGuard } from 'app/hooks/useUnsavedChangesGuard'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useWorkspace } from 'app/utils'
import { logError } from 'lib/logging'
import {
  FORM_TEMPLATES_QUERY,
  PAPERWORK_LOCATIONS_QUERY,
  PAPERWORK_TEMPLATE_QUERY,
  UPDATE_PAPERWORK_TEMPLATE_MUTATION,
} from 'app/components/paperwork/graphql'
import type { PaperworkBrandingLocationsQuery } from 'app/generated/gql/graphql'
import type { FormTemplateRow } from './useFormsList'
import { PACKET_EDITOR_PATH, type PacketRow } from './usePacketsList'

type Draft = {
  name: string
  description: string
  forms: PacketFormRef[]
  branding: PacketBranding
  appearance: Appearance
  welcome: PacketScreen | undefined
  completion: PacketScreen
}

type BrandingLocation = PaperworkBrandingLocationsQuery['locations']['hits'][number]
type LogoStatus = `none` | `checking` | `found` | `missing`

const DEFAULT_WELCOME: PacketScreen = {
  title: `Welcome`,
  body: `Please complete these forms before your visit. It takes about five minutes.`,
}
const DEFAULT_COMPLETION: PacketScreen = {
  title: `All done`,
  body: `Thank you. Your paperwork has been sent to the office.`,
}
const ALL = { page: 0, limit: 1000 }
/** The scheduler's logo endpoint is public, which is what lets a patient's browser load it. */
const LOCATION_LOGO_PATH = /\/api\/locations\/([^/]+)\/scheduling\/logo$/

function draftFrom(row: PacketRow): Draft {
  return {
    name: row.name,
    description: row.description ?? ``,
    forms: parsePacketForms(row.formsJson),
    branding: parseBranding(row.brandingJson) ?? { displayName: `` },
    appearance: parseAppearance(row.appearanceJson),
    welcome: parseScreen(row.welcomeJson),
    completion: parseScreen(row.completionJson) ?? DEFAULT_COMPLETION,
  }
}

export type UsePacketEditorReturn = {
  fetching: boolean
  notFound: boolean
  draft: Draft | undefined
  dirty: boolean
  saving: boolean
  forms: FormVersions<FormTemplateRow>[]
  pinned: (FormTemplateRow | undefined)[]
  locations: BrandingLocation[]
  locationId: string | undefined
  logoStatus: LogoStatus
  preview: PacketContent | undefined
  setField: <K extends keyof Draft>(key: K, value: Draft[K]) => void
  addForm: (formId: string) => void
  pinLatest: (index: number) => void
  moveForm: (index: number, to: number) => void
  removeForm: (index: number) => void
  chooseLocation: (location: BrandingLocation) => void
  toggleWelcome: (on: boolean) => void
  save: () => Promise<void>
  back: () => void
}

export function usePacketEditor(id: string): UsePacketEditorReturn {
  const workspace = useWorkspace()
  const [{ data, fetching, error }] = useQuery({ query: PAPERWORK_TEMPLATE_QUERY, variables: { id } })
  const [{ data: formData }] = useQuery({
    query: FORM_TEMPLATES_QUERY,
    variables: { status: `published`, archived: false, pagination: ALL },
    requestPolicy: `cache-and-network`,
  })
  const [{ data: locationData }] = useQuery({
    query: PAPERWORK_LOCATIONS_QUERY,
    variables: { search: `workspaceId:${workspace?.id}`, pagination: ALL },
    pause: !workspace?.id,
  })
  const [{ fetching: saving }, executeUpdate] = useMutation(UPDATE_PAPERWORK_TEMPLATE_MUTATION)

  const row = data?.paperworkTemplate
  const [draft, setDraft] = useState<Draft>()
  const [logoStatus, setLogoStatus] = useState<LogoStatus>(`none`)

  useEffect(() => {
    if (row) setDraft(draftFrom(row))
  }, [row?.id])

  const logoUrl = draft?.branding.logoUrl
  useEffect(() => {
    if (!logoUrl) {
      setLogoStatus(`none`)
      return
    }
    setLogoStatus(`checking`)
    const probe = new Image()
    probe.onload = () => setLogoStatus(`found`)
    probe.onerror = () => setLogoStatus(`missing`)
    probe.src = logoUrl
    return () => {
      probe.onload = null
      probe.onerror = null
    }
  }, [logoUrl])

  const publishedRows = formData?.formTemplates.hits ?? []
  const forms = groupFormVersions(publishedRows).sort((a, b) => a.latest.name.localeCompare(b.latest.name))
  const pinned = (draft?.forms ?? []).map((ref) =>
    publishedRows.find((r) => r.formId === ref.formId && r.formVersion === ref.formVersion),
  )
  const locations = locationData?.locations.hits ?? []
  const locationId = logoUrl?.match(LOCATION_LOGO_PATH)?.[1]

  const setField: UsePacketEditorReturn['setField'] = (key, value) =>
    setDraft((current) => current && { ...current, [key]: value })
  const patchForms = (change: (refs: PacketFormRef[]) => PacketFormRef[]) =>
    setDraft((current) => current && { ...current, forms: change(current.forms) })
  const latestPublished = (formId: string) => forms.find((f) => f.formId === formId)?.latest

  const dirty = !!row && !!draft && !sameDraft(draft, draftFrom(row))
  const leaveTo = useUnsavedChangesGuard(dirty, `your changes to this packet`)

  const save = async () => {
    if (!draft) return
    const displayName = draft.branding.displayName.trim()
    const { error: saveError } = await executeUpdate({
      id,
      input: {
        name: draft.name.trim() || `Untitled packet`,
        description: draft.description.trim() || undefined,
        forms: draft.forms,
        // A logo that fails to load would show a broken image on the patient's phone.
        branding: displayName
          ? { ...draft.branding, displayName, logoUrl: logoStatus === `missing` ? undefined : draft.branding.logoUrl }
          : undefined,
        appearance: draft.appearance,
        welcome: draft.welcome ?? null,
        completion: draft.completion,
      },
    })
    if (saveError) {
      notifyError(`Couldn't save the packet`)
      logError(`usePacketEditor: save failed`, saveError.message)
      return
    }
    notifySuccess(`Packet saved`)
  }

  const preview: PacketContent | undefined = draft && {
    name: draft.name,
    welcome: draft.welcome,
    completion: draft.completion,
    appearance: draft.appearance,
    branding: {
      ...draft.branding,
      displayName: draft.branding.displayName || draft.name,
      logoUrl: logoStatus === `found` ? draft.branding.logoUrl : undefined,
    },
    forms: pinned
      .filter((r): r is FormTemplateRow => r !== undefined)
      .map((r) => ({
        id: r.formId,
        name: r.name,
        category: r.category as FormCategory,
        version: r.formVersion,
        questions: parseQuestions(r.questionsJson),
      })),
  }

  return {
    fetching,
    notFound: !fetching && (!!error || !row),
    draft,
    dirty,
    saving,
    forms,
    pinned,
    locations,
    locationId,
    logoStatus,
    preview,
    setField,
    addForm: (formId) => {
      const latest = latestPublished(formId)
      if (latest) patchForms((refs) => [...refs, { formId, formVersion: latest.formVersion }])
    },
    pinLatest: (index) =>
      patchForms((refs) =>
        refs.map((ref, i) => {
          const latest = latestPublished(ref.formId)
          return i === index && latest ? { ...ref, formVersion: latest.formVersion } : ref
        }),
      ),
    moveForm: (index, to) => patchForms((refs) => moveItem(refs, index, to)),
    removeForm: (index) => patchForms((refs) => refs.filter((_, i) => i !== index)),
    chooseLocation: (location) =>
      setDraft(
        (current) =>
          current && {
            ...current,
            branding: {
              displayName: location.name ?? current.branding.displayName,
              phone: location.businessPhone ?? current.branding.phone,
              logoUrl: `${window.location.origin}/api/locations/${location.id}/scheduling/logo`,
            },
          },
      ),
    toggleWelcome: (on) => setField(`welcome`, on ? DEFAULT_WELCOME : undefined),
    save,
    back: () => leaveTo(PACKET_EDITOR_PATH),
  }
}
